"use client";

import * as React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import { cn } from "@/lib/utils";
import { BlogSummaryCard } from "@/components/frontend/entity/blog";
import type { CardVariant } from "@/components/frontend/ui/card";
import type { BadgeVariant } from "@/components/frontend/ui/badge/badge";
import { BlogRoute } from "@/lib/types/blog";
import { Blog, Card } from "@/payload-types";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";

const LG_SLIDES_PER_VIEW = { "2": 2, "3": 3, "4": 4 };
const LG_FALLBACK_AUTO_COLS: Record<number, string> = {
	1: "lg:auto-cols-[100%]",
	2: "lg:auto-cols-[calc((100%-24px)/2)]",
	3: "lg:auto-cols-[calc((100%-48px)/3)]",
	4: "lg:auto-cols-[calc((100%-72px)/4)]",
};

export function BlogsCarousel({
	posts,
	columns,
	className,
	blogRoutes,
	cardVariant,
	badgeVariant,
	cardSettings,
}: {
	posts: Partial<Blog>[];
	columns?: "2" | "3" | "4" | null;
	className?: string;
	blogRoutes?: Partial<Record<string, BlogRoute>>;
	cardVariant?: CardVariant;
	badgeVariant?: BadgeVariant;
	cardSettings?: Card["settings"];
}) {
	const [isSwiperReady, setIsSwiperReady] = React.useState(false);
	const lgSlidesPerView = Math.min(LG_SLIDES_PER_VIEW[columns ?? "3"], posts.length);

	if (!posts || posts.length === 0) return null;

	return (
		<div className={cn("relative w-full", className)}>
			{!isSwiperReady && (
				<div className="w-full overflow-hidden">
					<div
						className={cn(
							"grid grid-flow-col gap-3.5 auto-cols-[calc(100%/1.1)] sm:auto-cols-[calc(100%/1.3)] md:auto-cols-[calc(100%/2.1)] lg:gap-6",
							LG_FALLBACK_AUTO_COLS[lgSlidesPerView],
						)}
					>
						{posts.map((post) => (
							<div key={post.id} className="flex-col">
								<BlogSummaryCard
									cardSettings={cardSettings}
									post={post}
									urlBase={blogRoutes?.[resolveRelationshipId(post.blogType)]?.detailUrlBase ?? ""}
									variant={cardVariant}
									badgeVariant={badgeVariant}
								/>
							</div>
						))}
					</div>
				</div>
			)}

			<div className={cn("w-full overflow-hidden", !isSwiperReady && "absolute inset-0 opacity-0 pointer-events-none")} aria-hidden={!isSwiperReady}>
				<Swiper
					modules={[Navigation]}
					spaceBetween={14}
					slidesPerView={1.1}
					watchOverflow
					breakpoints={{
						640: { slidesPerView: 1.3 },
						768: { slidesPerView: 2.1 },
						1024: { slidesPerView: lgSlidesPerView, spaceBetween: 24 },
					}}
					onAfterInit={() => setIsSwiperReady(true)}
				>
					{posts.map((post) => (
						<SwiperSlide key={post.id} className="h-auto! flex! flex-col!">
							<BlogSummaryCard
								post={post}
								urlBase={blogRoutes?.[resolveRelationshipId(post.blogType)]?.detailUrlBase ?? ""}
								variant={cardVariant}
								badgeVariant={badgeVariant}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			</div>
		</div>
	);
}
